import React, { useContext, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { UeElement } from '@oneaudi/falcon-tools';
import type { HeadlineProps } from '@oneaudi/unified-web-components';
import { Headline } from '@oneaudi/unified-web-components';
import type { UnifiedThemeType } from '@oneaudi/unified-web-common';
import {
  SYS_BREAKPOINT_2_XL,
  SYS_BREAKPOINT_MD,
  SYS_COLOR_FOREGROUND_NEUTRAL_800_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  SYS_FONT_SIZES_RELATIVE_250,
  SYS_FONT_SIZES_RELATIVE_300,
  SYS_FONT_SIZES_RELATIVE_525,
  ThemeProvider,
} from '@oneaudi/unified-web-common';
import type { AppearanceProps, CtaButton, TexContentProps } from '../../../types';
import { ContentPosition, CTAsTheme, Mode } from '../../../types';
import StageButtons, { ResponsiveButtonsWrapper } from './StageButtons';
import type { Tracking } from '../../hooks/useTracking';
import StageLegal from '../stage-legal/StageLegal';
import StagePrices from '../stage-prices/StagePrices';
import { RichTextAnchor } from './RichText';
import { getPaddingBasedOnAsset } from '../../utils/getPaddingBasedOnAsset';
import { getThemeName } from '../../utils/alphaHelper';
import { ContentContext } from '../../ContentContext';
import { FascinationContext } from '../../context/FascinationContext';
import { sanitizeText } from '../../utils/textUtils';
import { renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';

const StyledContainer = styled.div`
  width: inherit;
`;

interface StageContentWrapProps {
  contentPosition?: ContentPosition;
  isVideoStage: boolean;
  isModelPage?: boolean;
  hideControls?: boolean;
}

const StageContentWrap = styled.div<StageContentWrapProps>`
  width: auto;
  overflow: hidden;
  max-width: 1920px;
  padding: 0 var(--page-margin);

  position: relative;
  z-index: 4 !important;
  white-space: pre-wrap;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    padding: 0 var(--page-margin)
      ${({ isVideoStage, hideControls, contentPosition }) => {
        const shouldUseDefault =
          hideControls && contentPosition === ContentPosition.BOTTOM_LEFT && isVideoStage;
        return getPaddingBasedOnAsset(isVideoStage, 'var(--spacing-relative-xl)', shouldUseDefault);
      }}
      var(--page-margin);
    text-align: ${({ contentPosition }) => contentPosition === ContentPosition.CENTER && 'center;'};
    background: ${({ isVideoStage, contentPosition }) =>
      isVideoStage && contentPosition === ContentPosition.BOTTOM_LEFT
        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.2) 10%, rgba(0, 0, 0, 0.4) 30%, rgba(0, 0, 0, 0.3) 60%, rgba(255, 255, 255, 0) 100%);'
        : 'transparent'};
    ${ResponsiveButtonsWrapper} {
      padding: 0;
    }
  }
  @media (max-width: ${SYS_BREAKPOINT_MD}) {
    ${ResponsiveButtonsWrapper} {
      padding: 0 0 var(--spacing-relative-lg) 0;
    }
  }
`;

type StyledHeadlineProps = {
  isModelPage?: boolean;
  fascinationMode: boolean;
  animate: boolean;
};

const StyledHeadline = styled(Headline)<StyledHeadlineProps>`
  margin-bottom: ${({ isModelPage }) => !isModelPage && 'var(--spacing-relative-md)'};
  font-size: ${({ isModelPage }) => isModelPage && SYS_FONT_SIZES_RELATIVE_250};

  @media (min-width: ${() => SYS_BREAKPOINT_MD}) {
    font-size: ${({ isModelPage }) => isModelPage && SYS_FONT_SIZES_RELATIVE_300};
  }
  @media (min-width: ${() => SYS_BREAKPOINT_2_XL}) {
    font-size: ${({ isModelPage }) => isModelPage && SYS_FONT_SIZES_RELATIVE_525};
  }

  ${({ fascinationMode, animate }) =>
    !fascinationMode
      ? ''
      : `@media (prefers-reduced-motion: no-preference) {
    @keyframes fadeInUp {
      to {
        transform: translateY(0px);
        opacity: 1;
      }
    }

    transform: translateY(18px);
    opacity: 0;
    ${animate ? 'animation: fadeInUp 600ms cubic-bezier(0.215, 0.61, 0.355, 1) 1.4s forwards;' : ''}
  }`}
`;

type StyledHeadlineSubLineWrapperType = {
  isVideoStage?: boolean;
  isModelPage?: boolean;
  appTheme: CTAsTheme;
  mode?: Mode;
};

const getThemeForHeadlines = (theme: CTAsTheme) => {
  if (process.env.IS_LEGACY) {
    return theme === CTAsTheme.Dark
      ? SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT
      : SYS_COLOR_FOREGROUND_NEUTRAL_800_DARK;
  } else return SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT;
};
// Sys.Typography.Headline.Order1
export const StyledHeadlineSubLineWrapper = styled.div<StyledHeadlineSubLineWrapperType>`
  pointer-events: auto;
  @media (max-width: ${SYS_BREAKPOINT_MD}) {
    margin-block-end: ${({ mode }) => (mode === Mode.SMALL ? 'var(--spacing-relative-2xl)' : 0)};
    #headline-data,
    ~ span#anchors-allowed > a,
    p,
    span {
      color: ${({ isVideoStage, isModelPage, appTheme }) =>
        isVideoStage &&
        !isModelPage &&
        appTheme !== CTAsTheme.Media &&
        getThemeForHeadlines(appTheme)};
    }
  }
`;

interface HeadlineSubLineWrapperProps {
  headline?: string;
  headingTag?: HeadlineProps['tag'];
  isModelPage?: boolean;
  mode?: Mode;
  showSubline?: boolean | '';
  subline?: string;
  isVideoStage?: boolean;
  appearance: AppearanceProps;
  tracking: Tracking;
  showPrices?: boolean;
}

const convertRichText = (richText: string) => {
  const sanitizedText = sanitizeText(richText);
  return renderTextWithFootnotesReferencesV2(sanitizedText);
};

export const HeadlineSubLineWrapper: React.FC<HeadlineSubLineWrapperProps> = ({
  appearance,
  headline,
  headingTag,
  mode,
  isModelPage,
  showSubline,
  subline,
  isVideoStage,
  tracking,
  showPrices,
}) => {
  const {
    content: { fascinationMode },
  } = useContext(ContentContext);
  const { animate } = useContext(FascinationContext);
  return (
    <StyledHeadlineSubLineWrapper
      isVideoStage={isVideoStage}
      isModelPage={isModelPage}
      mode={mode}
      appTheme={appearance.backgroundDisclaimersCTAs}
    >
      {headline && (
        <UeElement type="text" property="textHeadline" label="Headline">
          {(ueProps) => (
            <StyledHeadline
              tag={headingTag || 'h1'}
              id="headline-data"
              variant={{ order: '1', type: 'headline', weight: 'normal' }}
              margin={['var(--spacing-relative-xl)', 0, 'var(--spacing-relative-xs)', 0]}
              data-testid="headline-data"
              isModelPage={isModelPage}
              fascinationMode={fascinationMode}
              animate={animate}
              {...ueProps}
            >
              {convertRichText(headline)}
            </StyledHeadline>
          )}
        </UeElement>
      )}
      {showSubline && (
        <UeElement type="text" property="textSubline" label="Subline">
          {(ueProps) => (
            <RichTextAnchor
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              tag="p"
              id="anchors-allowed"
              tracking={tracking}
              variant={{ order: '4', type: 'headline', weight: 'normal' }}
              text={subline || ''}
              data-testid="subline-data"
              {...ueProps}
            />
          )}
        </UeElement>
      )}
      {showPrices && <StagePrices />}
    </StyledHeadlineSubLineWrapper>
  );
};

interface StageContentProps {
  text?: TexContentProps;
  buttons?: CtaButton[];
  mode?: Mode;
  consumptionAndEmissions?: string[];
  appearance: AppearanceProps;
  tracking: Tracking;
  isVideoStage: boolean;
  isMobile?: boolean;
  disclaimers?: string[];
  isModelPage?: boolean;
  showPrices?: boolean;
  hideControls?: boolean;
}

const StageContent: React.FC<StageContentProps> = ({
  text,
  buttons,
  mode,
  consumptionAndEmissions,
  appearance,
  tracking,
  isVideoStage = false,
  isMobile,
  disclaimers,
  isModelPage,
  showPrices,
  hideControls = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { registerRef, unregisterRef } = useContext(FascinationContext);
  const { headline, headingTag, subline } = text || {};
  const showSubline = subline && !showPrices;
  const { theme, backgroundDisclaimersCTAs, textContrast, contentPosition } = appearance;
  const overwrittenThemeName: UnifiedThemeType['name'] = textContrast
    ? 'light'
    : getThemeName(theme.toLowerCase() as UnifiedThemeType['name']);

  const isLegalBottomLeft =
    (backgroundDisclaimersCTAs === CTAsTheme.Media &&
      !isMobile &&
      contentPosition === ContentPosition.BOTTOM_LEFT) ||
    (isModelPage && !isMobile);

  useLayoutEffect(() => {
    registerRef(containerRef.current);

    return () => {
      unregisterRef();
    };
  }, []);

  return (
    <StyledContainer ref={containerRef} data-testid="stage-content">
      {/* delete padding bottom if mode large and bottom left */}
      <StageContentWrap
        data-testid="headline-subline-buttons-data"
        contentPosition={isModelPage ? ContentPosition.BOTTOM_LEFT : contentPosition}
        isVideoStage={isVideoStage}
        isModelPage={isModelPage}
        hideControls={hideControls}
      >
        <ThemeProvider theme={{ name: overwrittenThemeName }}>
          <HeadlineSubLineWrapper
            appearance={appearance}
            headline={headline}
            tracking={tracking}
            mode={mode}
            headingTag={headingTag || 'h1'}
            subline={subline}
            showSubline={showSubline}
            isModelPage={isModelPage}
            isVideoStage={isVideoStage}
            showPrices={showPrices}
          />
        </ThemeProvider>
        {!isMobile && <StageButtons buttons={buttons} tracking={tracking} />}
        {isLegalBottomLeft && (
          <StageLegal
            consumptionAndEmissions={consumptionAndEmissions}
            disclaimers={disclaimers}
            appearance={appearance}
            isModelPage={isModelPage}
            data-testid="stage-legal"
          />
        )}
      </StageContentWrap>
      {isMobile && (
        <StageButtons
          buttons={buttons}
          tracking={tracking}
          isMobile={isMobile}
          data-testid="stage-buttons"
        />
      )}
    </StyledContainer>
  );
};

export default StageContent;
